/**
 * The mobile Brief cards for the crowded-buckets read (issues #372, #386): which asset-class
 * buckets money moved INTO and which it LEFT, led by a plain-English takeaway (toward safety /
 * toward risk / mixed) -- one card for the latest trading day, one for the week, the day first.
 * Every word comes from src/shared/bucketCrowding.js.
 *
 * ONE card per window carrying both sides, on purpose. The Brief round-robins its sections and
 * loads in batches of 20, so a window split into an "into" card and a "left" card puts the
 * second half behind "Load more" -- and the read is the PAIR. Two WINDOWS are two independent
 * reads, so the week card landing in the next batch is fine; the day leads because the Brief
 * is about today.
 *
 * Built on the same payload (`/fear-greed/buckets`) and the same words
 * (`src/shared/bucketCrowding.js`) as the desktop "Asset classes" tab, so the two
 * surfaces cannot disagree. It describes where money went and how that compares with the
 * period before -- no forecast.
 *
 * Pure -- `App.js` feeds it the query data and drops the result into the Brief.
 */
import { WINDOWS, summarizeTilt, compareToPrevious, withBucketMeta } from '../shared/bucketCrowding';
import { shortDate } from './dateLabels';
import { bucketBarsVisual } from './briefVisuals';

// day first, then the week; the longer views live on desktop
const MOBILE_WINDOWS = ['1d', '1w'].map((key) => WINDOWS.find((w) => w.key === key)).filter(Boolean);
// the day card is dated; the week card says so ("week to Sep 21")
const cardLabel = (window, asOf) => `Where money moved · ${window.sessions === 1 ? '' : 'week to '}${shortDate(asOf)}`;

/**
 * @param data the `/fear-greed/buckets` payload
 * @returns Brief items, one per window the payload carries (day first). Empty when the read is
 *   missing, stale, or has no callouts. A side with no callouts (everything was sold, so nothing
 *   was "most bought") is left out, never padded.
 */
export function buildCrowdedBucketCards(data) {
  // a Brief card reads as "today": an old read must not be presented as current
  if (!data?.as_of || data.stale) return [];
  return MOBILE_WINDOWS.flatMap((window) => {
    const raw = data.windows?.[window.key]?.rows;
    if (!Array.isArray(raw)) return [];
    const rows = withBucketMeta(raw);
    const visual = bucketBarsVisual(rows);
    if (!visual) return [];

    const tilt = summarizeTilt(rows, window.period);
    // ONE line: the tape renders subtitles through renderMarketText, which collapses newlines.
    // The groups and their moves are drawn as bars (the visual), so the text carries only what
    // the bars cannot: the takeaway and how it compares with the period before.
    const expanded = [tilt.detail, compareToPrevious(rows, window)].filter(Boolean).join(' — ');

    return [{
      type: 'bucketFlow',
      id: `crowded-buckets-${window.key}-${data.as_of}`,
      label: cardLabel(window, data.as_of),
      title: tilt.headline,
      fullTitle: tilt.headline,
      fullSubtitle: expanded || undefined,
      visual,
    }];
  });
}
